import React, { useEffect, useMemo, useState } from "react";
import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

function toNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

const REQUIRED_TUNING_SCHEMA = {
  ranker_batch_size: {
    label: "Ranker Batch Size",
    description: "Antal bolag per ranker-loop. Exempel: input 200 och batch 20 ger 10 loopar.",
    min: 1,
    max: 100,
    default: 20,
  },
};

function normalizeTuningData(data) {
  const backendSchema = data?.schema || {};
  const mergedSchema = { ...backendSchema };
  Object.entries(REQUIRED_TUNING_SCHEMA).forEach(([key, meta]) => {
    if (!mergedSchema[key]) mergedSchema[key] = meta;
  });

  const backendValues = data?.values || {};
  const mergedValues = { ...backendValues };
  Object.entries(mergedSchema).forEach(([key, meta]) => {
    if (!Number.isFinite(Number(mergedValues[key]))) {
      mergedValues[key] = meta?.default ?? "";
    }
  });

  return { mergedSchema, mergedValues };
}

export default function AdminPipelineControlsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);
  const [schema, setSchema] = useState({});
  const [values, setValues] = useState({});

  const loadTuning = () => {
    setLoading(true);
    setError(null);
    apiFetch(`${getApiBase()}/admin/pipeline/tuning`)
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        const { mergedSchema, mergedValues } = normalizeTuningData(data);
        setSchema(mergedSchema);
        setValues(mergedValues);
      })
      .catch((e) => setError(e.message || "Kunde inte läsa pipeline-inställningar"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTuning();
  }, []);

  const schemaEntries = useMemo(
    () => Object.entries(schema || {}),
    [schema]
  );

  const metaByKey = useMemo(() => {
    const map = {};
    schemaEntries.forEach(([key, meta]) => {
      map[key] = meta || {};
    });
    return map;
  }, [schemaEntries]);

  const stageSections = useMemo(() => ([
    {
      id: "shortlist",
      title: "Steg 1 - Shortlist (company-shortlist)",
      description: "Tar fram kandidater från bolagsuniversum per trigger.",
      keys: ["shortlist_top_n"],
      helper: () => (
        `Utfall: max ${toNum(values.shortlist_top_n, 0)} bolag går vidare till ranker per trigger.`
      ),
    },
    {
      id: "ranker",
      title: "Steg 2 - Ranker (company-ranker)",
      description: "Poängsätter shortlistade bolag mot triggern i loopar.",
      keys: ["ranker_input_limit", "ranker_batch_size"],
      helper: () => {
        const inputLimit = Math.max(1, toNum(values.ranker_input_limit, 1));
        const batchSize = Math.max(1, toNum(values.ranker_batch_size, 1));
        const loops = Math.ceil(inputLimit / batchSize);
        return `Utfall: upp till ${inputLimit} bolag utvärderas, fördelat på cirka ${loops} loopar á ${batchSize}.`;
      },
    },
    {
      id: "verify",
      title: "Steg 3 - Verify (company-verify)",
      description: "Verifierar rankade bolag i batchar tills mål nås eller max loopar är uppnått.",
      keys: ["verify_batch_size", "verify_max_batches", "verify_target_keep"],
      helper: () => {
        const batchSize = Math.max(1, toNum(values.verify_batch_size, 1));
        const maxBatches = Math.max(1, toNum(values.verify_max_batches, 1));
        const capacity = batchSize * maxBatches;
        const targetKeep = Math.max(1, toNum(values.verify_target_keep, 1));
        return `Utfall: max ${maxBatches} loopar x ${batchSize} bolag (totalt ${capacity}). Stoppar tidigare vid ${targetKeep} keep.`;
      },
    },
  ]), [values]);

  const saveTuning = () => {
    setSaving(true);
    setError(null);
    setOk(null);
    apiFetch(`${getApiBase()}/admin/pipeline/tuning`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    })
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        const returnedValues = data?.values && typeof data.values === "object"
          ? data.values
          : {};
        const { mergedSchema, mergedValues } = normalizeTuningData({
          schema,
          values: {
            ...values,
            ...returnedValues,
          },
        });
        setSchema(mergedSchema);
        setValues(mergedValues);
        setOk("Pipeline-inställningar sparade.");
      })
      .catch((e) => setError(e.message || "Kunde inte spara pipeline-inställningar"))
      .finally(() => setSaving(false));
  };

  return (
    <section
      style={{
        background: "#fbfaf7",
        border: "1px solid rgba(17,17,17,0.10)",
        borderRadius: "22px",
        padding: "24px",
      }}
    >
      <h2 style={{ margin: 0, fontSize: "30px", fontWeight: 500 }}>Admin - pipelinekontroller</h2>
      <p style={{ color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif" }}>
        Justera gränser per steg och se hur de slår i praktiken per trigger.
      </p>

      {error && <p style={{ color: "#b42318", fontFamily: "Arial, Helvetica, sans-serif" }}>{error}</p>}
      {ok && <p style={{ color: "#0a7f5a", fontFamily: "Arial, Helvetica, sans-serif" }}>{ok}</p>}

      <div style={{ background: "#fff", border: "1px solid rgba(17,17,17,0.1)", borderRadius: "12px", padding: "14px", marginBottom: "18px" }}>
        <h3 style={{ marginTop: 0 }}>Pipelineparametrar</h3>
        {loading ? (
          <p style={{ margin: 0, color: "#5f5a52" }}>Laddar...</p>
        ) : (
          <>
            <div style={{ display: "grid", gap: "12px" }}>
              {stageSections.map((section) => (
                <div
                  key={section.id}
                  style={{
                    border: "1px solid rgba(17,17,17,0.1)",
                    borderRadius: "12px",
                    padding: "12px",
                    background: "#fcfbf8",
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: "3px" }}>{section.title}</div>
                  <div style={{ color: "#5f5a52", fontSize: "13px", marginBottom: "10px" }}>
                    {section.description}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: "10px", alignItems: "start" }}>
                    {section.keys.map((key) => {
                      const meta = metaByKey[key] || {};
                      return (
                        <React.Fragment key={key}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{meta?.label || key}</div>
                            <div style={{ color: "#5f5a52", fontSize: "13px", marginTop: "2px" }}>
                              {meta?.description || ""}
                            </div>
                            <div style={{ color: "#8a8278", fontSize: "12px", marginTop: "2px" }}>
                              {`Min ${meta?.min ?? "-"} · Max ${meta?.max ?? "-"} · Default ${meta?.default ?? "-"}`}
                            </div>
                          </div>
                          <input
                            type="number"
                            value={values[key] ?? ""}
                            min={meta?.min}
                            max={meta?.max}
                            onChange={(e) =>
                              setValues((prev) => ({
                                ...prev,
                                [key]: e.target.value === "" ? "" : Number(e.target.value),
                              }))
                            }
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              borderRadius: "10px",
                              border: "1px solid rgba(17,17,17,0.2)",
                              boxSizing: "border-box",
                            }}
                          />
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: "10px", color: "#3f3a34", fontSize: "12px", fontFamily: "Arial, Helvetica, sans-serif" }}>
                    {section.helper()}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
              <button
                type="button"
                disabled={saving}
                onClick={saveTuning}
                style={{
                  padding: "8px 12px",
                  borderRadius: "999px",
                  border: "1px solid #111",
                  background: "#111",
                  color: "#fff",
                  cursor: saving ? "wait" : "pointer",
                }}
              >
                {saving ? "Sparar..." : "Spara pipelineparametrar"}
              </button>
              <button
                type="button"
                onClick={loadTuning}
                style={{
                  padding: "8px 12px",
                  borderRadius: "999px",
                  border: "1px solid rgba(17,17,17,0.2)",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Ladda om
              </button>
            </div>
          </>
        )}
      </div>

    </section>
  );
}

